// 13s loop for the "Get served Intelligent performance insights" card (design
// handoff: insight-stream-animation.md). Three insight cards land one after
// another on a stack; each pops in with a spring and reveals its What / Why /
// Next rows in turn while older cards sit behind, smaller and dimmed. Pure
// frame function so the timeline can be unit tested.

export const INSIGHT_COUNT = 3;
export const INSIGHT_LOOP_SECONDS = 13;
/**
 * The animation starts with the first card already landed and fully revealed
 * (it begins 2.5s into the 13s handoff timeline), then plays on from there.
 */
export const INSIGHT_START_OFFSET = 2.5;
/** Length of one loop as played: the handoff timeline minus the skipped intro. */
export const INSIGHT_PLAYED_SECONDS =
  INSIGHT_LOOP_SECONDS - INSIGHT_START_OFFSET;
/** Reduced motion: all three cards landed and fully revealed, no fade. */
export const INSIGHT_REST_SECONDS = 11 - INSIGHT_START_OFFSET;

const CYCLE = 4;
const LAND = 1.5;
const FADE_OUT_START = INSIGHT_LOOP_SECONDS - 0.6;

const clamp = (x: number) => Math.min(1, Math.max(0, x));
const easeOut = (x: number) => 1 - (1 - clamp(x)) ** 3;
/** easeOutBack with c = 1.4. */
const back = (x: number) => {
  const c = 1.4;
  const s = clamp(x) - 1;
  return 1 + (c + 1) * s ** 3 + c * s ** 2;
};

export interface InsightCardFrame {
  visible: boolean;
  /** 0 = front of the stack. */
  depth: number;
  y: number;
  scale: number;
  opacity: number;
  zIndex: number;
  /** Opacity of the What, Why and Next (plus Ask) rows. */
  rows: [number, number, number];
}

export interface InsightFrame {
  frameOpacity: number;
  cards: InsightCardFrame[];
}

/** Fade-in after the stack has faded out and the loop restarts. */
const RESTART_FADE_IN = 0.3;

export function getInsightFrame(elapsedSeconds: number): InsightFrame {
  const loopIndex = Math.floor(elapsedSeconds / INSIGHT_PLAYED_SECONDS);
  const inLoop = elapsedSeconds - loopIndex * INSIGHT_PLAYED_SECONDS;
  const t = INSIGHT_START_OFFSET + inLoop;
  const landTime = (i: number) => i * CYCLE + LAND;
  const landed = Array.from(
    { length: INSIGHT_COUNT },
    (_, i) => t >= landTime(i)
  ).filter(Boolean).length;

  const cards = Array.from(
    { length: INSIGHT_COUNT },
    (_, i): InsightCardFrame => {
      const d = t - landTime(i);
      if (d < 0) {
        return {
          visible: false,
          depth: 0,
          y: 0,
          scale: 1,
          opacity: 0,
          zIndex: 0,
          rows: [0, 0, 0],
        };
      }
      const depth = landed - 1 - i;
      const slide = easeOut(d / 0.55);
      const p = back(d / 0.55);
      return {
        visible: true,
        depth,
        y: (1 - slide) * 22 - depth * 10,
        scale: (0.94 + 0.06 * p) * (1 - depth * 0.045),
        opacity:
          depth === 0 ? Math.min(1, slide * 1.4) : depth === 1 ? 0.55 : 0.25,
        zIndex: 10 - depth,
        rows: [
          easeOut((d - 0.2) / 0.35),
          easeOut((d - 0.4) / 0.35),
          easeOut((d - 0.6) / 0.35),
        ],
      };
    }
  );

  // The first play starts fully visible; later loops fade back in.
  const fadeIn = loopIndex > 0 ? clamp(inLoop / RESTART_FADE_IN) : 1;
  const fade = t > FADE_OUT_START ? 1 - (t - FADE_OUT_START) / 0.5 : fadeIn;

  return { frameOpacity: clamp(fade), cards };
}
